import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {SimpleUserResponse} from '@services/back-office/model/simpleUserResponse';
import {AssociationsService} from '@shared/services/associations.service';
import {UsersService} from '@shared/services/users.service';
import {SharedService} from '@shared/shared.service';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, EMPTY, Observable, startWith, Subject, takeUntil} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {UserInfoModel} from "@services/back-office/model/userInfoModel";
import {RoleModel} from "@services/back-office/model/roleModel";
import {UserModel} from "@services/back-office/model/userModel";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BillingCounterpartyModel} from '@services/back-office';

@Component({
    selector: 'app-counterparty-role-dialog2',
    templateUrl: './counterparty-role-dialog.component.html',
    styleUrls: ['./counterparty-role-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CounterpartyRoleDialogComponent2 implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    roleForm: UntypedFormGroup;
    /** User to add */
    userToAdd: SimpleUserResponse | null;
    /** Search User Finished */
    searchUserFinished = false;
    /** Roles and positions */
    positions$ = new BehaviorSubject<string[] | null | undefined>(null);
    filteredPositions$: Observable<string[]>;
    roles$ = new BehaviorSubject<RoleModel[] | null | undefined>(null);
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            counterparty: BillingCounterpartyModel,
            formType: 'setGroupMainAdmin' | 'hidden'
        },
        public dialogRef: MatDialogRef<CounterpartyRoleDialogComponent2>,
        private associationsService: AssociationsService,
        private usersService: UsersService,
        private formBuilder: UntypedFormBuilder,
        private changeDetectorRef: ChangeDetectorRef,
        private translateService: TranslateService,
        private toastrService: ToastrService,
    ) {
    }

    ngOnInit(): void {
        if (!this.roleForm) {
            this.initForm();
        }
        /** Get Roles */
        this.getRoles();
        /** Get Positions */
        this.getPositions();
        this.watchUserChanges();

        this.filteredPositions$ = this.roleForm.get('position')!.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || ''))
        );
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.loading$.complete();
        this.positions$.complete();
        this.roles$.complete();
    }

    initForm() {
        this.roleForm = this.formBuilder.group({
            phone: [''],
            firstName: [''],
            lastName: [''],
            position: [''],
            role: [''],
            manager: ['']
        });
    }

    /** Get Roles */
    getRoles() {
        this.associationsService.getAssociationRoles(this.data.counterparty.id!).pipe(
            take(1),
            map((response: RoleModel[]) => {
                this.roles$.next(response);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                return EMPTY;
            })
        ).subscribe();
    }

    /** Get Positions */
    getPositions() {
        this.associationsService.getAssociationPositions(this.data.counterparty.id!).pipe(
            take(1),
            map((response: string[]) => {
                this.positions$.next(response);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                return EMPTY;
            })
        ).subscribe();
    }

    private _filter(value: string): string[] {
        const filterValue = value.toLowerCase();
        const positions = this.positions$.value || [];
        return positions.filter(option => option.toLowerCase().includes(filterValue));
    }

    /** Додавання введеного значення до форми */
    onInput(event: Event): void {
        const inputElement = event.target as HTMLInputElement;

        if (inputElement && inputElement.value) {
            const value = inputElement.value;
            if (!this.positions$.value?.includes(value)) {
                this.roleForm.get('position')?.setValue(value);
            }
        }
    }

    watchUserChanges() {
        this.roleForm.controls['phone'].valueChanges.pipe(
            takeUntil(this.destroyed$),
        ).subscribe(selectedValue => {
            if (selectedValue?.length < 13) {
                this.userToAdd = null;
                this.searchUserFinished = false;
            }
        });
    }

    findUser() {
        const userPhone = this.roleForm.get('phone')?.value;
        if (userPhone) {
            this.usersService.findUser({phoneNumber: userPhone}).pipe(
                take(1),
                map((response: UserInfoModel) => {
                    this.userToAdd = response.profile!;
                    this.searchUserFinished = true;
                    this.changeDetectorRef.detectChanges();
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error.error.message === 'user_not_found') {
                        this.searchUserFinished = true;
                    } else {
                        SharedService.showErrorWithTitle(this.translateService, this.toastrService, error.error.message);
                    }
                    this.loading$.next(false);
                    this.changeDetectorRef.detectChanges();
                    return EMPTY;
                })
            ).subscribe();
        }
    }

    submitForm() {
        const formData = this.roleForm.getRawValue();

        if (this.data.formType == 'hidden' && !formData.role) {
            SharedService.showWarningWithTitle(
                this.translateService,
                this.toastrService,
                'modules.clients.associations.associationRoleDialog.noRoleWarning'
            );
            return;
        }

        if (this.data.formType == 'setGroupMainAdmin') {
            const request: UserModel = {
                ...this.userToAdd?.id && {userId: this.userToAdd.id},
                ...!this.userToAdd?.id && formData.firstName && formData.lastName && formData.phone && {
                    userName: {
                        firstName: formData.firstName,
                        lastName: formData.lastName
                    },
                    userLogin: {
                        number: formData.phone
                    }
                },
                ...formData.position && {position: formData.position},
            };

            this.loading$.next(true);

            this.associationsService.addAdminGovernment(this.data.counterparty.id!, request).pipe(
                take(1),
                map(() => {
                    this.loading$.next(false);
                    this.closeDialog(true);
                }),
                catchError((error: HttpErrorResponse) => {
                    this.loading$.next(false);
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    return EMPTY;
                })
            ).subscribe();
        }

        if (this.data.formType == 'hidden') {
            if (this.data.counterparty.id && this.roleForm.get('manager')?.value.id) {
                this.loading$.next(true);

                this.associationsService.addHidenGovernment(this.data.counterparty.id, this.roleForm.get('manager')?.value.id, this.roleForm.get('role')?.value.id).pipe(
                    take(1),
                    map(() => {
                        this.loading$.next(false);
                        this.closeDialog(true);
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.loading$.next(false);
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        return EMPTY;
                    })
                ).subscribe();
            }

        }


    }

    /** Take only last 10 digits from the phone clipboard data on Ctrl+v event */
    onInputPaste(clipboardData: string | undefined) {
        if (clipboardData) {
            const hasNumberReg = /\d/;
            const hasNumber = hasNumberReg.test(clipboardData);
            if (hasNumber) {
                const trimmedValue = clipboardData.replace(/\D/g, '');
                let tenDigitsFromTheEnd = trimmedValue.slice(-10);

                if (tenDigitsFromTheEnd && tenDigitsFromTheEnd.length === 9) {
                    tenDigitsFromTheEnd = `0${tenDigitsFromTheEnd}`;
                }

                if (tenDigitsFromTheEnd && tenDigitsFromTheEnd.length === 10) {
                    this.roleForm.patchValue({
                        phone: tenDigitsFromTheEnd
                    });
                }
            }
        }
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }

}
